import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Alert, Chip, IconButton, Tooltip,
  TextField, InputAdornment, Snackbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { API_BASE_URL } from "../../config";

const STATUS_CHIP = {
  active:    { bg: "#e8f5e9", color: "#2e7d32" },
  suspended: { bg: "#fdecea", color: "#c62828" },
  expired:   { bg: "#fff3e0", color: "#e65100" },
  pending:   { bg: "#f5f5f5", color: "#616161" },
};

export default function LicenceKeysPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [snack, setSnack] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/superadmin/licence-keys`);
      setKeys(res.data.keys || []);
    } catch {
      setError("Failed to load licence keys.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = keys.filter((k) => {
    const q = search.toLowerCase();
    return !q || k.slug?.toLowerCase().includes(q) || k.org_name?.toLowerCase().includes(q);
  });

  const handleCopy = (key) => {
    navigator.clipboard.writeText(key).then(() => setSnack("Copied to clipboard"));
  };

  const handleDownload = (k) => {
    const blob = new Blob([k.licenceKey], { type: "text/plain" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `${k.slug}.lic`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleRegenerate = async (k) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/superadmin/tenants/${k.id}/licence/regenerate`);
      const key = res.data?.licenceKey;
      if (key) {
        const blob = new Blob([key], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${k.slug}.lic`; a.click();
        URL.revokeObjectURL(url);
      }
      setSnack(
        res.data?.licenceEmailSent
          ? `Regenerated and emailed ${k.org_name}`
          : `Regenerated ${k.org_name} (email not sent — check SMTP)`
      );
      load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to regenerate licence.");
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ bgcolor: "#f9fafb", px: 4, py: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Licence Keys</Typography>
      <Typography color="text.secondary" fontSize="14px" mb={3}>
        Signed <code>.lic</code> files for dedicated (customer-cloud) tenants
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search tenant…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 280, bgcolor: "#fff" }}
        />
      </Box>

      <Card elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: 2, overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              {["Organisation", "Slug", "Cloud", "Hosting", "Status", "Expires", "Licence Key", "Actions"].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 600, fontSize: "12px", color: "#667085" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {search ? "No matching tenants." : "No dedicated tenants found."}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((k) => {
                const statusStyle = STATUS_CHIP[k.status] || STATUS_CHIP.pending;
                const keyPreview = k.licenceKey
                  ? k.licenceKey.slice(0, 18) + "…"
                  : "—";
                return (
                  <TableRow key={k.id} hover>
                    <TableCell sx={{ fontSize: "13px", fontWeight: 500 }}>{k.org_name}</TableCell>
                    <TableCell sx={{ fontSize: "12px", fontFamily: "monospace" }}>{k.slug}</TableCell>
                    <TableCell>
                      <Chip label={k.cloud?.toUpperCase()} size="small"
                        sx={{ bgcolor: "#f5f5f5", fontSize: "11px", fontWeight: 600 }} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={(k.hosting || "dedicated").toUpperCase()}
                        size="small"
                        sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontSize: "11px", fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={k.status} size="small"
                        sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, fontWeight: 600, fontSize: "11px", textTransform: "capitalize" }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: "12px" }}>
                      {k.contract_end ? new Date(k.contract_end).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "12px", fontFamily: "monospace" }}>{keyPreview}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Copy key">
                          <IconButton size="small" onClick={() => handleCopy(k.licenceKey)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download .lic">
                          <IconButton size="small" onClick={() => handleDownload(k)}>
                            <DownloadOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Regenerate & email tenant admin">
                          <IconButton size="small" onClick={() => handleRegenerate(k)}>
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={2500}
        onClose={() => setSnack("")}
        message={snack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
